<template>
  <div class="wc-tab">
    <div class="wc-stage">
      <button
        v-for="stage in stages"
        :key="stage.type"
        class="wc-stage__item"
        :class="{ 'is-active': activeType === stage.type }"
        @click="changeStage(stage.type)"
      >
        {{ stage.label }}
      </button>
    </div>
    <load-state :state="state" empty-text="暂无赛程" @retry="load(false)">
      <div
        v-for="day in scheduleDays"
        :key="day.schedule_date"
        ref="dateBlocks"
        class="wc-date-block"
      >
        <div class="wc-date-block__title" :class="{ 'is-today': isToday(day) }">
          <span>{{ dateTitle(day) }}</span>
          <span v-if="isToday(day)" class="wc-date-block__today">今天</span>
        </div>
        <match-card
          v-for="(match, matchIndex) in day.schedule_list"
          :key="matchKey(match, matchIndex)"
          :match="match"
        />
      </div>
    </load-state>
  </div>
</template>

<script>
import { getSchedule } from '@/api/worldcup2026'
import LoadState from '../components/LoadState.vue'
import MatchCard from '../components/MatchCard.vue'
import { formatDateTitle } from '../utils/time'
import { getMatchListKey } from '../utils/match'
import { worldcupCache } from '../utils/cache'

const LIVE_POLL_INTERVAL = 10 * 60 * 1000

export default {
  name: 'WorldCupScheduleTab',
  components: { LoadState, MatchCard },
  data () {
    return {
      stages: [
        { label: '小组赛', type: '1' },
        { label: '1/16 决赛', type: '2' },
        { label: '1/8 决赛', type: '3' },
        { label: '1/4 决赛', type: '4' },
        { label: '半决赛', type: '5' },
        { label: '季军赛', type: '6' },
        { label: '决赛', type: '7' }
      ],
      activeType: '',
      state: 'idle',
      scheduleDays: [],
      timer: null,
      visible: true,
      didScrollToToday: false,
      scrollingToToday: false
    }
  },
  created () {
    document.addEventListener('visibilitychange', this.handleVisibility)
    this.loadInitial()
  },
  beforeDestroy () {
    document.removeEventListener('visibilitychange', this.handleVisibility)
    this.stopPolling()
  },
  methods: {
    dateTitle (day) {
      return formatDateTitle(day)
    },
    isToday (day) {
      const current = day.schedule_current
      if (current !== undefined && current !== null && current !== '') {
        return String(current) === '1' || current === true
      }
      if (!day.schedule_date) return false
      return String(day.schedule_date).slice(0, 10) === this.todayDate()
    },
    todayDate () {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(new Date())
      const pick = type => {
        const part = parts.find(item => item.type === type)
        return part ? part.value : ''
      }
      return `${pick('year')}-${pick('month')}-${pick('day')}`
    },
    matchKey (match, index) {
      return getMatchListKey(match, index)
    },
    isScheduleType (days, type) {
      const matches = days.reduce((list, day) => list.concat(day.schedule_list || []), [])
      const typedMatches = matches.filter(match => match.match_type !== undefined && match.match_type !== null && match.match_type !== '')
      if (!typedMatches.length) return true
      return typedMatches.every(match => String(match.match_type) === String(type))
    },
    async loadInitial () {
      this.state = 'loading'
      try {
        const resp = await getSchedule()
        if (resp.error_code !== 0) {
          this.state = 'error'
          return
        }
        const currentType = resp.result && resp.result.ext && resp.result.ext.current_match_type
        this.activeType = currentType || '1'
        const days = resp.result && resp.result.data ? resp.result.data : []
        if (days.length > 0 && this.isScheduleType(days, this.activeType)) {
          this.applySchedule(days, true)
          worldcupCache.schedules[this.activeType] = days
          return
        }
        this.load(false)
      } catch (e) {
        this.state = 'error'
      }
    },
    async load (useCache = true, silent = false) {
      if (!this.activeType) this.activeType = '1'
      if (useCache && worldcupCache.schedules[this.activeType]) {
        this.applySchedule(worldcupCache.schedules[this.activeType], !silent && !this.didScrollToToday)
        return
      }
      const requestType = this.activeType
      if (!silent) this.state = 'loading'
      try {
        const resp = await getSchedule({ type: requestType })
        if (String(requestType) !== String(this.activeType)) return
        if (resp.error_code !== 0) {
          if (!silent || !this.scheduleDays.length) this.state = 'error'
          return
        }
        const days = resp.result && resp.result.data ? resp.result.data : []
        worldcupCache.schedules[requestType] = days
        this.applySchedule(days, !silent && !this.didScrollToToday)
      } catch (e) {
        if (!silent || !this.scheduleDays.length) this.state = 'error'
      }
    },
    applySchedule (days, scrollToToday = false) {
      this.scheduleDays = days
      this.state = days.length ? 'success' : 'empty'
      this.updatePolling()
      if (scrollToToday) this.scrollToToday()
    },
    scrollToToday () {
      if (this.didScrollToToday || this.scrollingToToday || !this.scheduleDays.length) return
      const todayIndex = this.scheduleDays.findIndex(day => this.isToday(day))
      if (todayIndex < 0) return
      this.scrollingToToday = true
      this.$nextTick(() => {
        const blocks = this.$refs.dateBlocks || []
        const target = Array.isArray(blocks) ? blocks[todayIndex] : blocks
        if (!target || typeof window === 'undefined') {
          this.scrollingToToday = false
          return
        }
        const header = document.querySelector('.wc-page__header')
        const headerHeight = header ? header.getBoundingClientRect().height : 0
        const targetTop = target.getBoundingClientRect().top + window.pageYOffset
        const scrollTop = Math.max(targetTop - headerHeight - 8, 0)
        window.scrollTo(0, scrollTop)
        this.didScrollToToday = true
        this.scrollingToToday = false
      })
    },
    changeStage (type) {
      if (this.activeType === type) return
      this.activeType = type
      this.load(true)
    },
    hasLiveMatch () {
      return this.scheduleDays.some(day => {
        return (day.schedule_list || []).some(match => String(match.match_status) === '2')
      })
    },
    updatePolling () {
      this.stopPolling()
      if (this.visible && this.hasLiveMatch()) {
        this.timer = setInterval(() => this.load(false, true), LIVE_POLL_INTERVAL)
      }
    },
    stopPolling () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    handleVisibility () {
      this.visible = !document.hidden
      this.updatePolling()
    }
  }
}
</script>

<style scoped lang="scss">
.wc-tab {
  padding: 12px;
}

.wc-stage {
  display: flex;
  gap: 8px;
  padding: 2px 0 12px;
  overflow-x: auto;
}

.wc-stage__item {
  flex: 0 0 auto;
  min-width: 72px;
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 17px;
  color: #6b7280;
  background: #ffffff;
  font-size: 13px;
}

.wc-stage__item.is-active {
  color: #5a4300;
  background: #f6c343;
  font-weight: 600;
}

.wc-date-block {
  margin-bottom: 16px;
}

.wc-date-block__title {
  padding: 4px 2px 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
}

.wc-date-block__title.is-today {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 4px 6px;
  color: #deba40;
  font-weight: 500;
}

.wc-date-block__today {
  padding: 2px 8px;
  border-radius: 10px;
  color: #0a1020;
  background: #b8880a;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
}
</style>
